import {
  EMPTY_BAR,
  applyOrder,
  barMenu,
  drinkDef,
  drinkPrice,
  hasDrink,
  isDrunk,
  orderRefusal,
  cooldownOf,
  settleWithDrinks,
  type BarState,
  type DrinkId,
  isTipsy,
  TIPSY_AT,
} from './bar';
import { JACKPOT_CONTRIBUTION, TOP_BUILT_FLOOR, floorDef, gamesOnFloor, jackpotSeed, nextFloor, type FloorDef } from './floors';
import { GAMES } from './games';
import type { ActResult, GameId, Gesture, TableEnv, TableOption } from './games/types';
import { Rng } from './rng';
import { CASINO_PRICE, MARKER_BETS, NO_PERKS, perksFor, shopItems, type RunPerks, type UpgradeDef, type UpgradeId } from './shop';
import { readJson, removeKey, writeJson } from './storage';

/**
 * The casino layer: run state, the hub, the shared bet editor, leaving a
 * table, and the bust rule. Everything here is pure (snapshot in, snapshot
 * out); AppGlasses persists the result after every gesture.
 *
 * Built so far: floors 1-3 with the elevator, House Specials, progressive
 * jackpots, the Bar (bar.ts), and the Gift Shop (shop.ts).
 */

/** Paired with a $300 first fare: the first climb is a 2x, not a 5x. The
 *  economy sim (SIM=1 npx vitest run src/game/economy.sim.test.ts) is how
 *  these were chosen. */
export const STARTING_BANKROLL = 150;

export interface TableSlot {
  /** Bet the player chose, in floor dollars. */
  bet: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any;
}

export interface RunStats {
  plays: number;
  peakBankroll: number;
  biggestWin: number;
  jackpotsWon: number;
  drinksOrdered: number;
}

export interface RunState {
  floor: number;
  bankroll: number;
  rngState: number;
  tables: Partial<Record<GameId, TableSlot>>;
  stats: RunStats;
  /** This floor's House Special: one game with better odds, re-picked on
   *  every floor you arrive at. */
  special: GameId;
  /** This floor's progressive jackpot pot. Fractional so small
   *  contributions still add up; shown rounded down. The elevator only goes
   *  up, so a pot left behind is simply gone. */
  jackpot: number;
  /** Buzz and active drinks. */
  bar: BarState;
  /** Gift Shop upgrades in force for this run, fixed when it started. */
  perks: RunPerks;
  /** Bought the casino: the run is won, and only the last shopping trip and
   *  the win screen are left. */
  ownsCasino: boolean;
  /** Testing only (no way to set it in the game): every table is the ★. */
  starAll?: boolean;
  /** The VIP Pass's marker has been used this run. */
  markerUsed?: boolean;
}

export interface MetaProgress {
  runsPlayed: number;
  bestPeakBankroll: number;
  highestFloor: number;
  /** Gift Shop purchases; they apply from the next run on. */
  upgrades: UpgradeId[];
  /** Runs won by buying the casino. */
  casinosBought: number;
}

/** 'owner': just bought the casino — shop once more, or finish. */
export type Screen = 'title' | 'resetConfirm' | 'newRunConfirm' | 'hub' | 'table' | 'betEdit' | 'elevator' | 'bar' | 'shop' | 'owner' | 'bust';

export interface GameSnapshot {
  screen: Screen;
  meta: MetaProgress;
  run: RunState | null;
  titleCursor: number;
  hubCursor: number;
  tableCursor: number;
  elevatorCursor: number;
  barCursor: number;
  /** A drink that would black you out, tapped once and waiting for a
   *  second tap to confirm. */
  barConfirm: DrinkId | null;
  shopCursor: number;
  /** An upgrade tapped once, waiting for the confirming second tap. */
  shopConfirm: UpgradeId | null;
  activeGame: GameId | null;
  /** Bet being edited (bet editor only). */
  pendingBet: number;
  /** One-shot notice shown on the current screen, cleared by the next gesture. */
  notice: string | null;
  /** Stats of the run that just ended (bust screen). */
  lastRun: RunStats | null;
  /** How that run ended. */
  lastRunEnd: 'bust' | 'blackout' | 'won' | null;
  /** Bankroll when that run ended (shown on the win screen). */
  lastRunCash: number;
  /** Cursor on the owner screen (Shop / I'm done). */
  ownerCursor: number;
  /** An animation playing at the table (see TableAnim), or none. */
  anim?: TableAnim | null;
}

/**
 * A play's animation (reels, cards, a burst): the frames play one per table
 * tick, then the new state shows. The money is already settled and saved;
 * only the display waits — the header keeps the bankroll from before the
 * play and the play's notices (drinks, jackpot) wait until the end. A tap
 * skips it. Not saved: reloading mid-animation just shows the result.
 */
export interface TableAnim {
  frames: string[];
  index: number;
  /** Bankroll to show in the header until it ends. */
  bankroll: number;
  /** The play's notice, shown when it ends. */
  notice: string | null;
}

/** Celebration frames after winning a floor's progressive jackpot. */
export function jackpotFrames(share: number): string[] {
  const amount = `+${usd(share)}`;
  return [
    ['', '   *   *   *   *   *', '', '     J A C K P O T', '', '   *   *   *   *   *'],
    ['', ' *   *   *   *   *   *', '', '    * JACKPOT! *', `      ${amount}`, ' *   *   *   *   *   *'],
    ['', '   *   *   *   *   *', '', '     J A C K P O T', `      ${amount}`, '   *   *   *   *   *'],
  ].map((lines) => lines.join('\n'));
}

/** The bankroll the header shows (held back while an animation plays). */
export function shownBankroll(snapshot: GameSnapshot): number {
  return snapshot.anim?.bankroll ?? snapshot.run?.bankroll ?? 0;
}

/** Ends an animation early (a tap) or when its last frame has played. */
function endAnim(snapshot: GameSnapshot): GameSnapshot {
  return { ...snapshot, notice: snapshot.anim?.notice ?? snapshot.notice, anim: null };
}

// ── Persistence ──

const META_KEY = 'feelin-lucky:meta';
const RUN_KEY = 'feelin-lucky:run';

export const DEFAULT_META: MetaProgress = { runsPlayed: 0, bestPeakBankroll: 0, highestFloor: 1, upgrades: [], casinosBought: 0 };

export function loadMeta(): MetaProgress {
  return { ...DEFAULT_META, ...(readJson<MetaProgress>(META_KEY) ?? {}) };
}

export function saveMeta(meta: MetaProgress): void {
  writeJson(META_KEY, meta);
}

export function loadRun(): RunState | null {
  const run = readJson<RunState>(RUN_KEY);
  if (!run) return null;
  // Saves from before floors/specials/jackpots existed.
  return {
    ...run,
    // Also covers a save whose Special is a game that has since been cut.
    special: run.special && GAMES[run.special] ? run.special : floorGames(run.floor)[0]!,
    jackpot: run.jackpot ?? jackpotSeed(run.floor),
    bar: { ...EMPTY_BAR, ...run.bar },
    perks: { ...NO_PERKS, ...run.perks },
    ownsCasino: run.ownsCasino ?? false,
    stats: { ...run.stats, jackpotsWon: run.stats.jackpotsWon ?? 0, drinksOrdered: run.stats.drinksOrdered ?? 0 },
  };
}

export function saveRun(run: RunState | null): void {
  if (run) writeJson(RUN_KEY, run);
  else removeKey(RUN_KEY);
}

export function initialGameSnapshot(meta: MetaProgress, run: RunState | null): GameSnapshot {
  return {
    screen: 'title',
    meta,
    run,
    titleCursor: 0,
    hubCursor: 0,
    tableCursor: 0,
    elevatorCursor: 0,
    barCursor: 0,
    barConfirm: null,
    shopCursor: 0,
    shopConfirm: null,
    activeGame: null,
    pendingBet: 0,
    notice: null,
    lastRun: null,
    lastRunEnd: null,
    lastRunCash: 0,
    ownerCursor: 0,
  };
}

// ── Bets ──

export function minBet(id: GameId, floor: number): number {
  return GAMES[id].baseMinBet * floorDef(floor).betMultiplier;
}

export function maxBet(id: GameId, floor: number): number {
  return GAMES[id].baseMaxBet * floorDef(floor).betMultiplier;
}

const NICE_STEPS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 75];

/** Round-number bet ladder between a game's floor min and max. */
export function betLadder(id: GameId, floor: number): number[] {
  const lo = minBet(id, floor);
  const hi = maxBet(id, floor);
  const ladder = new Set<number>([lo]);
  for (let scale = 1; scale <= hi; scale *= 10) {
    for (const step of NICE_STEPS) {
      const value = step * scale;
      if (value >= lo && value <= hi) ladder.add(value);
    }
  }
  ladder.add(hi);
  const step = (GAMES[id].betStep ?? 1) * floorDef(floor).betMultiplier;
  return [...ladder].filter((b) => b % step === 0).sort((a, b) => a - b);
}

/** Highest ladder bet that's <= the wanted amount and affordable. */
function affordableBet(id: GameId, floor: number, wanted: number, bankroll: number): number {
  const ladder = betLadder(id, floor);
  let best = ladder[0]!;
  for (const b of ladder) if (b <= wanted && b <= bankroll) best = b;
  return best;
}

/** Games on a floor: its own, the floors below, and any installed from the
 *  Gift Shop (installed games are on every floor, at that floor's prices). */
export function floorGames(floor: number, installed: readonly GameId[] = []): GameId[] {
  const games = gamesOnFloor(floor);
  return [...games, ...installed.filter((id) => !games.includes(id))].filter((id) => id in GAMES);
}

/** Scratchers is the last-chance game, never the House Special: at a $1-a-
 *  ticket stake a Special can't pay for anything (the ★ simulation found it
 *  was a trap that caused a quarter of all busts). */
const NEVER_SPECIAL: readonly GameId[] = ['scratchers'];

function pickSpecial(floor: number, rng: Rng, installed: readonly GameId[] = []): GameId {
  const games = floorGames(floor, installed).filter((id) => !NEVER_SPECIAL.includes(id));
  return games[rng.int(games.length)]!;
}

/** Cheapest bet anywhere on the current floor — below this, the run is over. */
export function cheapestBet(floor: number, installed: readonly GameId[] = []): number {
  return Math.min(...floorGames(floor, installed).map((id) => minBet(id, floor)));
}

// ── Table helpers ──

function tableSlot(run: RunState, id: GameId): TableSlot {
  return run.tables[id] ?? { bet: minBet(id, run.floor), state: GAMES[id].create() };
}

/** The floor's House Special. */
export function isSpecial(run: RunState, id: GameId): boolean {
  return run.special === id || !!run.starAll;
}

/**
 * A table's luck (games/luck.ts): 1 at the ★. Liquid Courage, while Tipsy:
 * I raises the ★ to 2 (~110%); III gives every other table ★ luck (1). (II
 * makes Tipsy start at Buzz 3.)
 */
export function tableLuck(run: RunState, id: GameId): number {
  const tipsy = isTipsy(run.bar);
  const courage = run.perks.courage ?? 0;
  if (isSpecial(run, id)) return courage >= 1 && tipsy ? 2 : 1;
  return courage >= 3 && tipsy ? 1 : 0;
}

function env(run: RunState, id: GameId, rng: Rng, now: number = Date.now()): TableEnv {
  const slot = tableSlot(run, id);
  const special = isSpecial(run, id);
  const luck = tableLuck(run, id);
  return { bankroll: run.bankroll, bet: slot.bet, minBet: minBet(id, run.floor), rng, now, special, luck };
}

// ── Bar ──

/** While you're Drunk, the bouncer keeps you off the floor's two tables with
 *  the highest minimum bets. */
export function bouncedTables(run: RunState): GameId[] {
  if (!isDrunk(run.bar)) return [];
  return [...floorGames(run.floor, run.perks.installed)].sort((a, b) => minBet(b, run.floor) - minBet(a, run.floor)).slice(0, 2);
}

/** "$4,906" — thousands separators for amounts in notices. */
function usd(n: number): string {
  return `$${Math.floor(n).toLocaleString('en-US')}`;
}

// ── Gift Shop ──

/** Buy the Casino: sits at the top of the Penthouse Gift Shop. Not an
 *  upgrade — buying it wins the run. */
export const CASINO_ITEM: UpgradeDef = {
  id: 'casino',
  name: 'Buy the Casino',
  effect: 'The whole place is yours. You win the run, then get one last shopping trip.',
  price: CASINO_PRICE,
  unlockFloor: TOP_BUILT_FLOOR,
};

export function shopShelf(snapshot: GameSnapshot): UpgradeDef[] {
  const shelf = shopItems(snapshot.meta.upgrades, snapshot.meta.highestFloor);
  const run = snapshot.run;
  return run && run.floor === TOP_BUILT_FLOOR && !run.ownsCasino ? [CASINO_ITEM, ...shelf] : shelf;
}

/** Buys the highlighted upgrade — on the second tap, since it spends real
 *  run money on something that only pays off next run. */
function buyUpgrade(snapshot: GameSnapshot): GameSnapshot {
  const run = snapshot.run!;
  const item = shopShelf(snapshot)[snapshot.shopCursor];
  if (!item) return snapshot;
  if (item.price > run.bankroll) return { ...snapshot, notice: `That's ${usd(item.price)}. You have ${usd(run.bankroll)}.` };
  if (snapshot.shopConfirm !== item.id) {
    const left = run.bankroll - item.price;
    const risky = !run.ownsCasino && item.id !== 'casino' && left < cheapestBet(run.floor, run.perks.installed) ? ' That ends this run!' : '';
    return { ...snapshot, shopConfirm: item.id, notice: `Buy for ${usd(item.price)}? You'd have ${usd(left)}.${risky} Tap again.` };
  }
  if (item.id === 'casino') {
    return {
      ...snapshot,
      screen: 'owner',
      ownerCursor: 0,
      shopConfirm: null,
      run: { ...run, bankroll: run.bankroll - item.price, ownsCasino: true },
      notice: null,
    };
  }
  const meta: MetaProgress = { ...snapshot.meta, upgrades: [...snapshot.meta.upgrades, item.id] };
  const shelf = shopItems(meta.upgrades, meta.highestFloor);
  const next: GameSnapshot = {
    ...snapshot,
    meta,
    shopConfirm: null,
    shopCursor: Math.min(snapshot.shopCursor, Math.max(0, shelf.length - 1)),
    run: { ...run, bankroll: run.bankroll - item.price },
    notice: `Bought ${item.name}. It starts next run.`,
  };
  // The victory lap can spend down to $0: the run is already won.
  return run.ownsCasino ? next : checkBust(next);
}

/** Leaves the victory lap for the win screen. */
function finishWin(snapshot: GameSnapshot): GameSnapshot {
  return snapshot.run ? endRun(snapshot, snapshot.run, 'won') : snapshot;
}

function orderDrink(snapshot: GameSnapshot): GameSnapshot {
  const run = snapshot.run!;
  const def = barMenu(run.floor, run.perks.recipes)[snapshot.barCursor];
  if (!def) return snapshot;
  const price = drinkPrice(def, floorDef(run.floor).betMultiplier, run.bar);
  const refusal = orderRefusal(def, run.bar, price, run.bankroll);
  if (refusal === 'money') return { ...snapshot, notice: `That's ${usd(price)}. You have ${usd(run.bankroll)}.` };
  if (refusal === 'cooldown') return { ...snapshot, notice: `${def.name} is sold out. Back in ${cooldownOf(run.bar, def.id)} plays.` };
  if (refusal === 'already') return { ...snapshot, notice: `You already have ${/^[AEIOU]/.test(def.name) ? 'an' : 'a'} ${def.name} going.` };
  if (refusal === 'slots') return { ...snapshot, notice: 'Hands full: 3 drinks going. Wait for one to wear off.' };
  const blacksOut = run.bar.buzz + def.buzz >= run.bar.maxBuzz;
  if (blacksOut && snapshot.barConfirm !== def.id) {
    return { ...snapshot, barConfirm: def.id, notice: 'This one will black you out and end the run. Tap again to drink it.' };
  }
  const bar = applyOrder(run.bar, def);
  const next: GameSnapshot = {
    ...snapshot,
    barConfirm: null,
    run: { ...run, bankroll: run.bankroll - price, bar, stats: { ...run.stats, drinksOrdered: run.stats.drinksOrdered + 1 } },
    notice: `${def.name}. Cheers! (-${usd(price)})`,
  };
  if (bar.buzz >= bar.maxBuzz) return endRun(next, next.run!, 'blackout');
  // Spending the last of your money at the bar can end the run too.
  return checkBust(next);
}

/** The table's option list as shown: the game's own options, plus the shared
 *  Bet option whenever a new bet could be placed. */
export function tableOptions(snapshot: GameSnapshot): TableOption[] {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (!run || !id) return [];
  if (snapshot.anim) return [{ id: 'skip', label: '...' }];
  const game = GAMES[id];
  const slot = tableSlot(run, id);
  const opts = game.options(slot.state, env(run, id, new Rng(run.rngState)));
  if (!game.fixedBet && !game.locked(slot.state)) opts.push({ id: 'bet', label: `Bet: $${slot.bet}` });
  // Between plays, a waiter brings the Bar to the table (user, 2026-09-23:
  // saves leaving for the hub to order).
  if (!game.locked(slot.state)) opts.push({ id: 'waiter', label: 'Call waiter' });
  return opts;
}

export function tableDescription(snapshot: GameSnapshot): string {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (!run || !id) return '';
  if (snapshot.anim) return snapshot.anim.frames[snapshot.anim.index] ?? '';
  const slot = tableSlot(run, id);
  const highlighted = tableOptions(snapshot)[snapshot.tableCursor]?.id;
  return GAMES[id].describe(slot.state, env(run, id, new Rng(run.rngState)), highlighted);
}

export function tableLocked(snapshot: GameSnapshot): boolean {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (!run || !id) return false;
  return !!snapshot.anim || GAMES[id].locked(tableSlot(run, id).state);
}

// ── Run lifecycle ──

/** A fresh run with every Gift Shop upgrade owned so far applied. Exported
 *  (with an optional seed) for the simulations. */
export function newRun(meta: MetaProgress, seed?: number): RunState {
  const rng = seed === undefined ? Rng.fromTime() : new Rng(seed);
  const perks = perksFor(meta.upgrades ?? []);
  const floor = 1;
  const special = pickSpecial(floor, rng, perks.installed);
  const bankroll = STARTING_BANKROLL + perks.startingBonus;
  let bar: BarState = {
    ...EMPTY_BAR,
    maxBuzz: perks.maxBuzz,
    tipsyAt: perks.courage >= 2 ? 3 : TIPSY_AT,
    drunkAt: perks.drunkAt,
    restockCut: perks.restockCut,
    soberEvery: perks.soberEvery,
    slots: perks.drinkSlots,
    priceFactor: perks.drinkPriceFactor,
  };
  if (perks.welcomeDrink) bar = applyOrder(bar, drinkDef('espresso'));
  return {
    floor,
    bankroll,
    rngState: rng.state,
    tables: {},
    stats: { plays: 0, peakBankroll: bankroll, biggestWin: 0, jackpotsWon: 0, drinksOrdered: 0 },
    special,
    jackpot: jackpotSeed(floor),
    bar,
    perks,
    ownsCasino: false,
  };
}

// ── Elevator ──

/** Below this many minimum bets of runway upstairs, the elevator warns. */
export const RUNWAY_WARNING = 10;

export interface ElevatorQuote {
  to: FloorDef;
  cost: number;
  after: number;
  cheapestUp: number;
  /** The median table minimum upstairs — what a normal bet costs there. */
  typicalUp: number;
  /** How many typical bets upstairs you could still afford. Measured
   *  against a real table, not the cheapest scratch ticket: the climb
   *  simulation found "10 scratchers of runway" often meant 2 hands of
   *  blackjack, and most runs that rode on it busted upstairs. */
  runway: number;
  /** Riding would leave you unable to place a single bet up there. */
  blocked: boolean;
  warn: boolean;
}

export function elevatorQuote(run: RunState): ElevatorQuote | null {
  const to = nextFloor(run.floor);
  if (!to) return null;
  const discount = 1 - run.perks.elevatorDiscount;
  const cost = Math.round(to.elevatorCost * discount);
  const after = run.bankroll - cost;
  const cheapestUp = cheapestBet(to.id, run.perks.installed);
  const typicalUp = typicalBet(to.id, run.perks.installed);
  const runway = Math.max(0, Math.floor(after / typicalUp));
  // Blocked only if you couldn't place even the cheapest bet up there.
  const blocked = after < cheapestUp;
  return { to, cost, after, cheapestUp, typicalUp, runway, blocked, warn: !blocked && runway < RUNWAY_WARNING };
}

/** Median table minimum on a floor (upper median for an even count). */
export function typicalBet(floor: number, installed: readonly GameId[] = []): number {
  const mins = floorGames(floor, installed)
    .map((id) => minBet(id, floor))
    .sort((a, b) => a - b);
  return mins[Math.floor(mins.length / 2)]!;
}

function rideElevator(snapshot: GameSnapshot): GameSnapshot {
  const run = snapshot.run!;
  const quote = elevatorQuote(run);
  if (!quote || quote.blocked) return snapshot;
  const floor = quote.to.id;
  const rng = new Rng(run.rngState);
  // Saved bets were in the old floor's dollars; start every table at the
  // new floor's minimum. Game state (history, shoe) carries over.
  const tables: RunState['tables'] = {};
  for (const [id, slot] of Object.entries(run.tables) as [GameId, TableSlot][]) tables[id] = { ...slot, bet: minBet(id, floor) };
  const special = pickSpecial(floor, rng, run.perks.installed);
  return {
    ...snapshot,
    screen: 'hub',
    hubCursor: 0,
    meta: { ...snapshot.meta, highestFloor: Math.max(snapshot.meta.highestFloor, floor) },
    run: {
      ...run,
      floor,
      bankroll: quote.after,
      rngState: rng.state,
      tables,
      special,
      jackpot: jackpotSeed(floor),
      bar: run.bar,
    },
    notice: `Welcome to the ${quote.to.name}.`,
  };
}

// ── Hub ──

export type HubItem = { kind: 'game'; id: GameId } | { kind: 'bar' } | { kind: 'shop' } | { kind: 'elevator' };

export function hubItems(run: RunState): HubItem[] {
  const items: HubItem[] = floorGames(run.floor, run.perks.installed).map((id) => ({ kind: 'game', id }));
  items.push({ kind: 'bar' }, { kind: 'shop' });
  if (nextFloor(run.floor)) items.push({ kind: 'elevator' });
  return items;
}

function endRun(snapshot: GameSnapshot, run: RunState, how: 'bust' | 'blackout' | 'won' = 'bust'): GameSnapshot {
  const meta: MetaProgress = {
    ...snapshot.meta,
    runsPlayed: snapshot.meta.runsPlayed + 1,
    casinosBought: (snapshot.meta.casinosBought ?? 0) + (how === 'won' ? 1 : 0),
    bestPeakBankroll: Math.max(snapshot.meta.bestPeakBankroll, run.stats.peakBankroll),
    highestFloor: Math.max(snapshot.meta.highestFloor, run.floor),
  };
  return { ...snapshot, screen: 'bust', meta, run: null, activeGame: null, lastRun: run.stats, lastRunEnd: how, lastRunCash: run.bankroll };
}

/** The bust rule: out of money for even the cheapest bet on this floor.
 *  Only checked between hands, never while a table is locked. */
function checkBust(snapshot: GameSnapshot): GameSnapshot {
  const run = snapshot.run;
  if (!run || tableLocked(snapshot)) return snapshot;
  if (run.bankroll >= cheapestBet(run.floor, run.perks.installed)) return snapshot;
  // VIP Pass: once a run, the house fronts a marker instead of ending it.
  if (run.perks.marker && !run.markerUsed) {
    const marker = MARKER_BETS * typicalBet(run.floor, run.perks.installed);
    const note = `VIP marker: the house fronts you $${marker}.`;
    return { ...snapshot, run: { ...run, bankroll: run.bankroll + marker, markerUsed: true }, notice: snapshot.notice ? `${snapshot.notice} · ${note}` : note };
  }
  return endRun(snapshot, run);
}

function startRun(snapshot: GameSnapshot): GameSnapshot {
  return { ...snapshot, screen: 'hub', run: newRun(snapshot.meta), hubCursor: 0, activeGame: null, lastRun: null };
}

// ── Title ──

export function titleItems(snapshot: GameSnapshot): { id: 'continue' | 'new' | 'reset'; label: string }[] {
  const items: { id: 'continue' | 'new' | 'reset'; label: string }[] = [];
  if (snapshot.run) items.push({ id: 'continue', label: `Continue (${usd(snapshot.run.bankroll)})` });
  items.push({ id: 'new', label: snapshot.run ? 'New run' : 'Start a run' });
  items.push({ id: 'reset', label: 'Reset progress' });
  return items;
}

// ── Gesture handlers ──

function wrap(index: number, length: number): number {
  return length === 0 ? 0 : (index + length) % length;
}

export function handleMove(prev: GameSnapshot, direction: 'up' | 'down'): GameSnapshot {
  if (prev.screen === 'table' && prev.anim) return prev; // let it play
  const snapshot = { ...prev, notice: null };
  const taken = gameGesture(snapshot, direction);
  if (taken) return taken;
  const step = direction === 'up' ? -1 : 1;
  switch (snapshot.screen) {
    case 'title':
      return { ...snapshot, titleCursor: wrap(snapshot.titleCursor + step, titleItems(snapshot).length) };
    case 'resetConfirm':
    case 'newRunConfirm':
      return { ...snapshot, titleCursor: wrap(snapshot.titleCursor + step, 2) };
    case 'hub':
      return snapshot.run ? { ...snapshot, hubCursor: wrap(snapshot.hubCursor + step, hubItems(snapshot.run).length) } : snapshot;
    case 'elevator':
      return { ...snapshot, elevatorCursor: wrap(snapshot.elevatorCursor + step, 2) };
    case 'bar':
      return snapshot.run
        ? { ...snapshot, barConfirm: null, barCursor: wrap(snapshot.barCursor + step, barMenu(snapshot.run.floor, snapshot.run.perks.recipes).length) }
        : snapshot;
    case 'shop':
      return { ...snapshot, shopConfirm: null, shopCursor: wrap(snapshot.shopCursor + step, shopShelf(snapshot).length) };
    case 'owner':
      return { ...snapshot, ownerCursor: wrap(snapshot.ownerCursor + step, 2) };
    case 'table':
      return { ...snapshot, tableCursor: wrap(snapshot.tableCursor + step, tableOptions(snapshot).length) };
    case 'betEdit': {
      const run = snapshot.run;
      const id = snapshot.activeGame;
      if (!run || !id) return snapshot;
      const ladder = betLadder(id, run.floor).filter((b) => b <= run.bankroll);
      const i = Math.max(0, ladder.indexOf(snapshot.pendingBet));
      // Scrolling up raises the bet; clamps at both ends rather than wrapping.
      const next = ladder[Math.min(ladder.length - 1, Math.max(0, i - step))] ?? snapshot.pendingBet;
      return { ...snapshot, pendingBet: next };
    }
    default:
      return snapshot;
  }
}

export function handleConfirm(prev: GameSnapshot): GameSnapshot {
  if (prev.screen === 'table' && prev.anim) return endAnim(prev); // tap skips
  const snapshot = { ...prev, notice: null };
  const taken = gameGesture(snapshot, 'tap');
  if (taken) return taken;
  switch (snapshot.screen) {
    case 'title': {
      const item = titleItems(snapshot)[snapshot.titleCursor];
      if (item?.id === 'continue') return { ...snapshot, screen: snapshot.run?.ownsCasino ? 'owner' : 'hub' };
      if (item?.id === 'new') {
        if (snapshot.run) return { ...snapshot, screen: 'newRunConfirm', titleCursor: 1 };
        return startRun(snapshot);
      }
      if (item?.id === 'reset') return { ...snapshot, screen: 'resetConfirm', titleCursor: 1 };
      return snapshot;
    }
    case 'resetConfirm':
      if (snapshot.titleCursor === 0) return { ...initialGameSnapshot({ ...DEFAULT_META }, null) };
      return { ...snapshot, screen: 'title', titleCursor: 0 };
    case 'newRunConfirm':
      // Abandoning a run in progress counts as a finished run.
      if (snapshot.titleCursor === 0 && snapshot.run) return startRun(endRun(snapshot, snapshot.run));
      return { ...snapshot, screen: 'title', titleCursor: 0 };
    case 'hub': {
      const run = snapshot.run;
      const item = run ? hubItems(run)[snapshot.hubCursor] : undefined;
      if (!run || !item) return snapshot;
      if (item.kind === 'elevator') {
        const quote = elevatorQuote(run);
        // Land on "Stay" whenever the ride is risky or impossible.
        return { ...snapshot, screen: 'elevator', elevatorCursor: !quote || quote.blocked || quote.warn ? 1 : 0 };
      }
      if (item.kind === 'bar') return { ...snapshot, screen: 'bar', barCursor: 0, barConfirm: null };
      if (item.kind === 'shop') return { ...snapshot, screen: 'shop', shopCursor: 0, shopConfirm: null };
      const id = item.id;
      if (bouncedTables(run).includes(id)) return { ...snapshot, notice: "The bouncer won't seat you like this. Sober up first." };
      if (run.bankroll < minBet(id, run.floor)) return { ...snapshot, notice: `Need $${minBet(id, run.floor)} to sit down.` };
      // Keep the saved bet affordable before showing the table.
      const slot = tableSlot(run, id);
      const bet = affordableBet(id, run.floor, slot.bet, run.bankroll);
      return {
        ...snapshot,
        screen: 'table',
        activeGame: id,
        tableCursor: 0,
        run: { ...run, tables: { ...run.tables, [id]: { ...slot, bet } } },
      };
    }
    case 'table':
      return confirmTableOption(snapshot);
    case 'betEdit': {
      const run = snapshot.run;
      const id = snapshot.activeGame;
      if (!run || !id) return snapshot;
      const slot = tableSlot(run, id);
      return { ...snapshot, screen: 'table', run: { ...run, tables: { ...run.tables, [id]: { ...slot, bet: snapshot.pendingBet } } } };
    }
    case 'bar':
      return snapshot.run ? orderDrink(snapshot) : snapshot;
    case 'shop':
      return snapshot.run ? buyUpgrade(snapshot) : snapshot;
    case 'owner':
      // "Shop" (one last trip with the leftover cash) or "I'm done".
      return snapshot.ownerCursor === 0 ? { ...snapshot, screen: 'shop', shopCursor: 0, shopConfirm: null } : finishWin(snapshot);
    case 'elevator':
      if (snapshot.elevatorCursor === 0) {
        const quote = snapshot.run ? elevatorQuote(snapshot.run) : null;
        // The screen already says why; a blocked ride just does nothing.
        if (quote?.blocked) return snapshot;
        return rideElevator(snapshot);
      }
      return { ...snapshot, screen: 'hub' };
    case 'bust':
      return { ...snapshot, screen: 'title', titleCursor: 0, lastRun: null };
    default:
      return snapshot;
  }
}

function confirmTableOption(snapshot: GameSnapshot): GameSnapshot {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (!run || !id) return snapshot;
  const option = tableOptions(snapshot)[snapshot.tableCursor];
  if (!option) return snapshot;
  if (option.id === 'waiter') return { ...snapshot, screen: 'bar', barCursor: 0, barConfirm: null };
  if (option.id === 'bet') {
    return { ...snapshot, screen: 'betEdit', pendingBet: affordableBet(id, run.floor, tableSlot(run, id).bet, run.bankroll) };
  }

  const game = GAMES[id];
  const slot = tableSlot(run, id);
  const rng = new Rng(run.rngState);
  const tableEnv = env(run, id, rng);
  // Starting a new play needs the bet on hand; re-fit it if the bankroll shrank.
  if (!game.locked(slot.state)) {
    if (run.bankroll < tableEnv.minBet) return { ...snapshot, notice: `Need $${tableEnv.minBet} to play.` };
    tableEnv.bet = affordableBet(id, run.floor, slot.bet, run.bankroll);
  }
  const next = applyResult(snapshot, id, game.act(slot.state, option.id, tableEnv), tableEnv.bet, rng);
  // Stay on the same option if it still exists (cycling a setting like
  // Crash's Auto, or hitting again); if the list changed shape (Deal ->
  // Hit/Stand), start from the top.
  const sameOption = next.screen === 'table' ? tableOptions(next).findIndex((o) => o.id === option.id) : -1;
  return { ...next, tableCursor: Math.max(0, sameOption) };
}

/** Commits a game's result into the run: bankroll, RNG, stats, bust check. */
function applyResult(snapshot: GameSnapshot, id: GameId, result: ActResult<unknown>, bet: number, rng: Rng): GameSnapshot {
  const run = snapshot.run!;
  const stats = { ...run.stats };
  let bankroll = result.bankroll;
  let jackpot = run.jackpot;
  let jackpotShare = 0;
  let bar = run.bar;
  const notes: string[] = [];
  if (result.settled) {
    const { wagered, returned } = result.settled;
    stats.plays += 1;
    jackpot += wagered * JACKPOT_CONTRIBUTION;
    // Drinks and Buzz act on every settled play (bonuses, refunds, drunk
    // tips and dropped chips), then tick down.
    // Only a real play moves restocks along: at least the table's own
    // minimum, and never a scratch ticket (the cheap filler this rule is
    // for). It used to be "at least the floor's usual bet", which left
    // tables with lower minimums (Baccarat's $40 on the Mezzanine, vs the
    // usual $50) unable to restock at all — user, 2026-09-25.
    const counts = id !== 'scratchers' && wagered >= minBet(id, run.floor);
    const drinks = settleWithDrinks(bar, wagered, returned, Math.min(minBet(id, run.floor), bankroll), rng.next(), counts, !!GAMES[id].noDealer);
    bankroll = Math.max(0, bankroll + drinks.delta);
    bar = drinks.bar;
    notes.push(...drinks.notes);
    stats.biggestWin = Math.max(stats.biggestWin, returned - wagered + drinks.delta);
  }
  if (result.jackpot) {
    const { share, pot } = jackpotPayout(jackpot, bet, maxBet(id, run.floor), run.floor);
    jackpotShare = share;
    bankroll += share;
    jackpot = pot;
    stats.jackpotsWon += 1;
    notes.unshift(`JACKPOT! +$${share}`);
  }
  stats.peakBankroll = Math.max(stats.peakBankroll, bankroll);
  // Never leave a bet on the table the bankroll can't cover — it would show
  // as "Bet: $25" while you hold $7. Lower it and say so the moment the play
  // is over; bust handling takes over if even the minimum is gone.
  const tableMax = maxBet(id, run.floor);
  if (!GAMES[id].locked(result.state) && (bet > bankroll || bet > tableMax) && bankroll >= minBet(id, run.floor)) {
    bet = affordableBet(id, run.floor, Math.min(bet, tableMax), bankroll);
    notes.push(`Bet lowered to $${bet}.`);
  }
  const nextRun: RunState = {
    ...run,
    bankroll,
    jackpot,
    bar,
    rngState: rng.state,
    stats,
    tables: { ...run.tables, [id]: { bet, state: result.state } },
  };
  const notice = notes.length ? notes.join(' · ') : snapshot.notice;
  const next = checkBust({ ...snapshot, run: nextRun, notice });
  // Play the game's frames (and a jackpot celebration) before the result;
  // skipped if the play ended the run.
  const frames = [...(result.frames ?? []), ...(jackpotShare > 0 ? jackpotFrames(jackpotShare) : [])];
  if (!frames.length || next.screen !== 'table') return next;
  return { ...next, notice: null, anim: { frames, index: 0, bankroll: snapshot.anim?.bankroll ?? run.bankroll, notice: next.notice } };
}

/**
 * The full pot needs the table's max bet (like a real progressive); smaller
 * bets win a proportional share and the rest stays in the pot. A full win
 * reseeds the pot.
 */
export function jackpotPayout(pot: number, bet: number, max: number, floor: number): { share: number; pot: number } {
  const fraction = Math.min(1, bet / max);
  const share = Math.floor(pot * fraction);
  return { share, pot: fraction >= 1 ? jackpotSeed(floor) : pot - share };
}

/** Routes a gesture to a game that takes over controls mid-play (Mines).
 *  Returns null when the game doesn't want it, so the default applies. */
function gameGesture(snapshot: GameSnapshot, gesture: Gesture): GameSnapshot | null {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (snapshot.screen !== 'table' || !run || !id) return null;
  const game = GAMES[id];
  if (!game.gesture) return null;
  const slot = tableSlot(run, id);
  const rng = new Rng(run.rngState);
  const result = game.gesture(slot.state, gesture, env(run, id, rng));
  return result ? applyResult(snapshot, id, result, slot.bet, rng) : null;
}

/** Real-time step for games like Crash, called on a short timer. Returns
 *  the same snapshot object when nothing changed, so the lens isn't redrawn. */
export function handleTick(snapshot: GameSnapshot, now: number): GameSnapshot {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (snapshot.screen !== 'table' || !run || !id) return snapshot;
  // An animation steps one frame per tick, and holds the game's own tick.
  if (snapshot.anim) {
    const index = snapshot.anim.index + 1;
    return index >= snapshot.anim.frames.length ? endAnim(snapshot) : { ...snapshot, anim: { ...snapshot.anim, index } };
  }
  const game = GAMES[id];
  if (!game.tick) return snapshot;
  const slot = tableSlot(run, id);
  const rng = new Rng(run.rngState);
  const result = game.tick(slot.state, env(run, id, rng, now));
  if (!result) return snapshot;
  // The option list can change shape on a tick (an AI's turn ends and yours
  // begins); keep the cursor on a real option.
  const next = applyResult(snapshot, id, result, slot.bet, rng);
  return next.screen === 'table' ? { ...next, tableCursor: Math.min(next.tableCursor, Math.max(0, tableOptions(next).length - 1)) } : next;
}

export function tableLeftPane(snapshot: GameSnapshot): string | null {
  const run = snapshot.run;
  const id = snapshot.activeGame;
  if (!run || !id) return null;
  return GAMES[id].leftPane?.(tableSlot(run, id).state) ?? null;
}

export function tableLockedHint(snapshot: GameSnapshot): string {
  if (snapshot.anim) return '• skip';
  return (snapshot.activeGame && GAMES[snapshot.activeGame].lockedHint) || '(finish this play first)';
}

export function handleBack(prev: GameSnapshot): GameSnapshot {
  if (prev.screen === 'table' && prev.anim) return prev;
  const snapshot = { ...prev, notice: null };
  const taken = gameGesture(snapshot, 'double');
  if (taken) return taken;
  switch (snapshot.screen) {
    case 'resetConfirm':
    case 'newRunConfirm':
      return { ...snapshot, screen: 'title', titleCursor: 0 };
    case 'hub':
      return { ...snapshot, screen: 'title', titleCursor: 0 };
    case 'table':
      if (tableLocked(snapshot)) return { ...snapshot, notice: 'Finish this play first.' };
      return { ...snapshot, screen: 'hub', activeGame: null };
    case 'betEdit':
      return { ...snapshot, screen: 'table' };
    case 'elevator':
      return { ...snapshot, screen: 'hub' };
    case 'bar':
      // Called to a table (a waiter): back to that table, not the hub.
      // The cursor goes back to the top (Deal, Spin...): you came to play.
      if (snapshot.activeGame) return { ...snapshot, screen: 'table', barConfirm: null, tableCursor: 0 };
      return { ...snapshot, screen: 'hub', barConfirm: null };
    case 'shop':
      // After buying the casino, leaving the shop ends the victory lap.
      if (snapshot.run?.ownsCasino) return finishWin({ ...snapshot, shopConfirm: null });
      return { ...snapshot, screen: 'hub', shopConfirm: null };
    case 'owner':
      return finishWin(snapshot);
    case 'bust':
      return { ...snapshot, screen: 'title', titleCursor: 0, lastRun: null };
    default:
      return snapshot;
  }
}

